const EXCLUDED_SECTIONS = new Set(['apiInterfaceConfig', 'deviceConfig'])

// Recursively collect all leaf dot-paths from a parsed config object,
// excluding apiInterfaceConfig and deviceConfig (handled on their own tabs).
export function buildCheckedSetFromConfig(obj, prefix = '') {
    const paths = new Set()
    if (!obj || typeof obj !== 'object') return paths

    for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key
        if (!prefix && EXCLUDED_SECTIONS.has(key)) continue
        if (Array.isArray(value)) {
            paths.add(path)
        } else if (value !== null && typeof value === 'object') {
            for (const p of buildCheckedSetFromConfig(value, path)) paths.add(p)
        } else {
            paths.add(path)
        }
    }
    return paths
}

// Build a filtered schema containing only sections/fields whose dot-paths are in checkedPaths.
// Excludes apiInterfaceConfig and deviceConfig — those are handled on dedicated tabs.
export function buildFilteredSchema(fullSchema, checkedPaths) {
    if (!fullSchema?.properties?.config?.properties) return fullSchema

    const filtered = structuredClone(fullSchema)
    const sectionMap = filtered.properties.config.properties

    for (const sectionKey of Object.keys(sectionMap)) {
        if (EXCLUDED_SECTIONS.has(sectionKey)) {
            delete sectionMap[sectionKey]
            continue
        }

        const section = sectionMap[sectionKey]
        if (!section?.properties) continue

        for (const subKey of Object.keys(section.properties)) {
            const sub = section.properties[subKey]
            if (sub?.type === 'object' && sub.properties) {
                for (const fieldKey of Object.keys(sub.properties)) {
                    const dotPath = `${sectionKey}.${subKey}.${fieldKey}`
                    if (!checkedPaths.has(dotPath)) {
                        delete sub.properties[fieldKey]
                    }
                }
                if (Object.keys(sub.properties).length === 0) {
                    delete section.properties[subKey]
                }
            } else {
                const dotPath = `${sectionKey}.${subKey}`
                if (!checkedPaths.has(dotPath)) {
                    delete section.properties[subKey]
                }
            }
        }

        if (Object.keys(section.properties).length === 0) {
            delete sectionMap[sectionKey]
        }
    }

    return filtered
}

// --- API Interface helpers ---
// dot-path: apiInterface.<modelName>.<fieldKey>

// The server merges API interfaces on the compound key cdType + taskDesc
// (ComponentConfig.MergePartialConfigJson). Both must be present in the saved
// template or ApplyConfigTemplate throws rather than skipping the entry, so
// these are always retained in the filtered schema and never offered as
// checkboxes — the user cannot deselect them into an unmergeable template.
export function isApiInterfaceKeyField(fieldKey, field) {
    if (!field) return false
    const isDiscriminator = field['readonly'] === true && field['s4-model-key'] != null
    return isDiscriminator || fieldKey === 'taskDesc'
}

function getApiInterfaceModelName(itemSchema) {
    for (const field of Object.values(itemSchema.properties || {})) {
        if (field['s4-model-name']) return field['s4-model-name']
    }
    return null
}

// Collect checked dot-paths from a stored apiInterfaceConfig.apiInterface array.
// Paths: apiInterface.<modelName>.<fieldKey>
export function buildApiInterfaceCheckedSetFromConfig(apiInterfaceArray) {
    const paths = new Set()
    if (!Array.isArray(apiInterfaceArray)) return paths
    for (const item of apiInterfaceArray) {
        if (!item || typeof item !== 'object') continue
        const modelName = item['s4-model-name'] || item.modelName || item.cdType
        if (modelName == null) continue
        for (const fieldKey of Object.keys(item)) {
            paths.add(`apiInterface.${modelName}.${fieldKey}`)
        }
    }
    return paths
}

// Build a filtered apiInterfaceConfig schema containing only the checked interface types/fields.
// Returns the items array portion of the schema (for the polymorphic apiInterface field).
// Always keeps the merge key fields (cdType discriminator and taskDesc) per item type.
export function buildFilteredApiInterfaceSchema(fullSchema, checkedPaths) {
    const items = fullSchema?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items
    if (!Array.isArray(items)) return []

    return structuredClone(items)
        .map(item => {
            const modelName = getApiInterfaceModelName(item)
            if (!modelName) return null

            for (const fieldKey of Object.keys(item.properties || {})) {
                const field = item.properties[fieldKey]
                if (isApiInterfaceKeyField(fieldKey, field)) continue
                if (!checkedPaths.has(`apiInterface.${modelName}.${fieldKey}`)) {
                    delete item.properties[fieldKey]
                }
            }

            // Key fields ride along with a real selection rather than constituting one,
            // so an interface type with nothing else checked is left out entirely.
            const hasCheckedFields = Object.keys(item.properties || {}).some(fieldKey => {
                const field = item.properties[fieldKey]
                if (isApiInterfaceKeyField(fieldKey, field)) return false
                return checkedPaths.has(`apiInterface.${modelName}.${fieldKey}`)
            })

            return hasCheckedFields ? item : null
        })
        .filter(Boolean)
}

// --- Device helpers ---
// dot-path: device.<deviceTypeName>.<fieldKey>, with a segment appended per level
// of the untitled object wrappers device schemas nest fields in (connection,
// manualCardEntry, emv, privateLabel, and emv.emvAidsReactUi a level deeper).
// Paths always address leaf fields, matching ConfigTemplateDeviceChecklist.
// deviceSchemas: array of { name, schemaValue } from deviceSchemasSignal

// Internal plumbing the user should never pick: the readonly discriminators
// (cdType, threadType) that identify the device type, and hidden fields. These
// are kept in the filtered schema — JSONForm needs them to save a valid device —
// but are not offered in the checklist.
export function isInternalDeviceField(field) {
    if (!field) return false
    return field.type === 'hidden' || (field.readonly === true && field.title == null)
}

// The server merges devices on the compound key deviceType + terminalList
// (ComponentConfig.MergePartialConfigJson). Both must be present in the saved
// template or ApplyConfigTemplate throws rather than skipping the entry, so
// these are always retained in the filtered schema and never offered as
// checkboxes. Both names occur only at the top level of the device schemas,
// so the depth check keeps a same-named nested field from matching by accident.
export function isDeviceKeyField(fieldKey, isTopLevel) {
    if (!isTopLevel) return false
    return fieldKey === 'deviceType' || fieldKey === 'terminalList'
}

// Device Name (taskDesc) is required by the device schemas and identifies the device
// within the template, but it is not part of the merge key — so unlike deviceType and
// terminalList it stays selectable. It is checked by default so a device is never
// built without it by accident.
const DEFAULT_CHECKED_DEVICE_FIELDS = ['taskDesc']

// Add the fields that should start checked for a device type to an existing selection.
export function withDefaultCheckedDeviceFields(deviceTypeName, checkedPaths) {
    const next = new Set(checkedPaths)
    for (const fieldKey of DEFAULT_CHECKED_DEVICE_FIELDS) {
        next.add(`device.${deviceTypeName}.${fieldKey}`)
    }
    return next
}

// The individual EMV AID inputs (emvAidsReactUi.*) are UI-only: their onChange handlers
// concatenate them into emv.aidSettings, and that single string is what the UTG actually
// reads. Selecting any field in the emv-aids group therefore has to bring aidSettings
// along, or the template would carry AID inputs the UTG never consumes.
export const EMV_AIDS_GROUP_ID = 'emv-aids'
const EMV_AID_SETTINGS_FIELD = 'aidSettings'

// The dot-path of the aidSettings field for a device type, or null when the schema has
// no EMV AID support. Derived from the schema rather than hardcoded so a device whose
// aidSettings sits somewhere other than emv.* still resolves correctly.
export function findEmvAidSettingsPath(deviceTypeName, configProps) {
    let found = null
    const walk = (props, prefix) => {
        for (const [fieldKey, field] of Object.entries(props || {})) {
            const dotPath = `${prefix}.${fieldKey}`
            if (fieldKey === EMV_AID_SETTINGS_FIELD && field?.['s4-group-id'] === EMV_AIDS_GROUP_ID) {
                found = found ?? dotPath
            }
            if (field?.type === 'object' && field.properties) walk(field.properties, dotPath)
        }
    }
    walk(configProps, `device.${deviceTypeName}`)
    return found
}

// The AID inputs are named <brand>_<Field> (amex_CheckBox, amex_OfflineFloorLimit, ...),
// one cluster of five per card brand. A brand's limits are only meaningful alongside its
// checkbox, so the whole cluster is selected or cleared together.
// Returns a Map of brand key -> array of dot-paths, keyed off the leaf field name.
export function groupEmvAidPathsByBrand(inputPaths) {
    const byBrand = new Map()
    for (const dotPath of inputPaths) {
        const fieldKey = dotPath.slice(dotPath.lastIndexOf('.') + 1)
        const separator = fieldKey.indexOf('_')
        if (separator <= 0) continue
        const brand = fieldKey.slice(0, separator)
        if (!byBrand.has(brand)) byBrand.set(brand, [])
        byBrand.get(brand).push(dotPath)
    }
    return byBrand
}

// Collect checked dot-paths from a stored deviceConfig.device array.
export function buildDeviceCheckedSetFromConfig(deviceArray) {
    const paths = new Set()
    if (!Array.isArray(deviceArray)) return paths

    const walk = (obj, prefix) => {
        for (const [key, value] of Object.entries(obj)) {
            const path = `${prefix}.${key}`
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                walk(value, path)
            } else {
                paths.add(path)
            }
        }
    }

    for (const item of deviceArray) {
        if (!item || typeof item !== 'object') continue
        const deviceType = item.deviceType
        if (!deviceType) continue
        walk(item, `device.${deviceType}`)
    }
    return paths
}

// Build a filtered schema for a single device type containing only checked fields.
// schemaValue: the schemaValue from deviceSchemasSignal for this device type.
// Returns a schema with .properties.config.properties filtered to checked fields only,
// or null when nothing is checked for this device type. Internal fields and the
// deviceType/terminalList merge keys are always retained, so emptiness is judged by
// the checked selection rather than by the remaining property count.
export function buildFilteredDeviceSchema(deviceTypeName, schemaValue, checkedPaths) {
    if (!schemaValue?.properties?.config?.properties) return null
    const filtered = structuredClone(schemaValue)
    const props = filtered.properties.config.properties

    let keptCount = 0

    // Prune unchecked leaves, then drop wrapper objects left empty by the pruning.
    const prune = (currentProps, prefix, isTopLevel) => {
        for (const fieldKey of Object.keys(currentProps)) {
            const field = currentProps[fieldKey]
            const dotPath = `${prefix}.${fieldKey}`
            if (field?.type === 'object' && field.properties) {
                prune(field.properties, dotPath, false)
                if (Object.keys(field.properties).length === 0) delete currentProps[fieldKey]
            } else if (isInternalDeviceField(field) || isDeviceKeyField(fieldKey, isTopLevel)) {
                continue
            } else if (checkedPaths.has(dotPath)) {
                keptCount++
            } else {
                delete currentProps[fieldKey]
            }
        }
    }

    prune(props, `device.${deviceTypeName}`, true)
    if (keptCount === 0) return null
    return filtered
}
